/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChild } from 'preact';
import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { useId } from '../UNSAFE_useId';
import { Floating, Placement, Coords } from '../../UNSAFE_Floating';
import { Layer } from '../../UNSAFE_Layer';
import { useHover } from '../UNSAFE_useHover';
import { useFocus } from '../UNSAFE_useFocus';
import { useTouch } from '../UNSAFE_useTouch';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import { TooltipContent } from './TooltipContent';
import { EnvironmentProvider } from '../../UNSAFE_Environment';
import { useColorScheme } from '../UNSAFE_useColorScheme';
import { TestIdProps } from '../UNSAFE_useTestId';

export const positions = [
  'start',
  'top-start',
  'top',
  'top-end',
  'end',
  'bottom-end',
  'bottom',
  'bottom-start'
] as const;
type Position = (typeof positions)[number];

const positionToPlacement = (position: Position) => {
  let placement: Placement = 'bottom';

  switch (position) {
    case 'top':
    case 'end':
    case 'bottom':
    case 'start':
      placement = position as Placement;
      break;

    case 'top-end':
      placement = 'top-end-corner';
      break;

    case 'bottom-end':
      placement = 'bottom-end-corner';
      break;

    case 'bottom-start':
      placement = 'bottom-start-corner';
      break;

    case 'top-start':
      placement = 'top-start-corner';
      break;
  }

  return placement;
};

export const anchorTos = ['element', 'pointer'] as const;
type AnchorTo = (typeof anchorTos)[number];

type AnchorModel = { x: AnchorTo; y: AnchorTo };

type Props = {
  text?: string;
  variant?: 'tooltip' | 'datatip';
  isOpen: boolean;
  position?: Position;
  isDisabled?: boolean;
  anchor?: AnchorModel;
  offset?: { mainAxis?: number; crossAxis?: number };
  onToggle?: (details: { value: boolean }) => void;
} & TestIdProps;

/**
 * The 'mounting' and 'unmounting' states are related to the animation. The 'unmounted' state
 * is used for conditional rendering. In this state, the Tooltip content is completely removed
 * from the DOM. The 'mountPending' and 'unmountPending' are used when timeout is active.
 * The 'reposition' state is used to trigger a transition with new coordinates within
 * the same element.
 */
type Status =
  | 'unmounted'
  | 'mountPending'
  | 'mounting'
  | 'mounted'
  | 'unmountPending'
  | 'unmounting';

export const useTooltipControlled = ({
  text,
  isOpen = false,
  variant = 'tooltip',
  position = 'bottom',
  isDisabled = false,
  anchor = { x: 'element', y: 'element' },
  offset = { mainAxis: 0, crossAxis: 0 },
  onToggle,
  testId
}: Props): {
  tooltipContent: ComponentChild;
  tooltipProps: Record<string, any>;
} => {
  const disabled = isDisabled || !text;
  const isInitialRenderState = useRef<boolean>(true);
  const hiddenWithEsc = useRef<boolean>(false);

  const [state, _setState] = useState<Status>(!disabled && isOpen ? 'mounting' : 'unmounted');
  const stateRef = useRef<Status>(state);
  const setState = (newState: Status) => {
    stateRef.current = newState;
    _setState(newState);
  };

  const [needsUpdate, setNeedsUpdate] = useState<boolean>(false);
  setNeedsUpdate(false);

  const isDatatip = variant === 'datatip';

  const isInitialRenderTrigger = useRef<boolean>(true);
  const { hoverProps, isHover } = useHover({});
  const { touchProps, isTouch } = useTouch({ isDisabled }); // TODO replace by useLongPress actionhook
  // we only care about keyboard focus, don't enable useFocus hook if isHover or isTouch are active
  const { focusProps, isFocus } = useFocus({ isDisabled: isHover || isTouch });
  const { hoverProps: popoverHoverProps, isHover: popoverIsHover } = useHover({ isDisabled });

  const isPointer = anchor.x === 'pointer' || anchor.y === 'pointer';

  const uniqueIdRef = useRef<string>(useId());
  const targetRef = useRef<HTMLElement | null>(null);
  const coordsRef = useRef<Coords>({ x: -9999, y: -9999 });
  // if anchor is set to pointer and target does not have keyboard focus, use coords
  const usedRef = isPointer && !isFocus ? coordsRef : targetRef;
  const delay = isDatatip ? 0 : 250;
  const toggleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentColorScheme = useColorScheme();
  const newColorScheme = !currentColorScheme || currentColorScheme === 'light' ? 'dark' : 'light';

  const toggleTimeoutClear = () => {
    if (toggleTimeoutRef.current) {
      clearTimeout(toggleTimeoutRef.current);
      toggleTimeoutRef.current = null;
    }
  };

  const updateState = useCallback(
    (open: boolean) => {
      if (open) {
        // At least one toggle event is set to true
        switch (state) {
          case 'unmounted':
            setState('mountPending'); // Set mount pending state if tooltip is unmounted
            break;
          case 'unmounting': // Switch back to mounting when unmounting was in progress and the tooltip should stay on
            setState('mounting');
            onToggle?.({ value: true });
            break;
          case 'unmountPending':
            toggleTimeoutClear();
            setState('mounted'); // Clear timers immediately and keep the tooltip mounted
            break;
        }
      } else {
        // No toggle events are set to true
        switch (state) {
          case 'mounting':
          case 'mounted':
            setState('unmountPending'); // Set unmount pending state if tooltip is mounted or in mounting transition
            break;
          case 'mountPending':
            toggleTimeoutClear();
            __resetCoords();
            setState('unmounted'); // Unmount tooltip immediately if mounting is pending
            break;
        }
      }
    },
    [state, onToggle]
  );

  useEffect(() => {
    if (isInitialRenderState.current) {
      // On onitial render do not trigger rerender
      isInitialRenderState.current = false;
      return;
    }
    setState(isOpen ? 'mounting' : 'unmounting');
  }, [isOpen]);

  useEffect(() => {
    // make sure to clear the toggle timeout on unmount
    return () => {
      toggleTimeoutClear();
    };
  }, []);

  useEffect(() => {
    if (isInitialRenderTrigger.current) {
      // On onitial render do not trigger rerender
      isInitialRenderTrigger.current = false;
      return;
    }
    // When pointer is used, handleMouseMove method is responsible for state updates
    // until the tooltip is mounted
    if (isPointer && isHover && state === 'unmounted') {
      return;
    }
    updateState(isHover || (isFocus && !hiddenWithEsc.current) || popoverIsHover || isTouch);
  }, [isHover, isFocus, popoverIsHover, isTouch, isPointer, state, updateState]);

  /*
   * Set a timer to toggle the state afer the 'pending' period expires
   */
  useEffect(() => {
    if (!toggleTimeoutRef.current && (state === 'mountPending' || state === 'unmountPending')) {
      toggleTimeoutRef.current = setTimeout(() => {
        toggleTimeoutClear();
        // make sure the state has not changed in the mean time before onToggle
        if (
          (state === 'mountPending' || state === 'unmountPending') &&
          state === stateRef.current
        ) {
          onToggle?.({ value: state === 'mountPending' });
        }
      }, delay);
    }
    return () => {
      toggleTimeoutClear();
    };
  }, [state, delay, onToggle]);

  const handleMouseMove = (event: MouseEvent) => {
    // we only want track mouse move while the state is 'unmounted' or when moving a 'mounted' datatip
    if (state !== 'unmounted' && !(variant === 'datatip' && state === 'mounted')) {
      return;
    }
    const targetRect = targetRef?.current?.getBoundingClientRect();

    // Tooltip maintains static offset from left side of window
    const distanceX = document.body.scrollLeft + (targetRect?.left || 0) + (targetRect?.width || 0);

    // Tooltip maintains static offset from top side of the window
    const distanceY = document.body.scrollTop + (targetRect?.top || 0) + (targetRect?.height || 0);

    // Display tooltip at the mouse position if both anchors are set to pointer
    const x = anchor.x === 'pointer' ? event.clientX : distanceX;
    const y = anchor.y === 'pointer' ? event.clientY : distanceY;

    if (state === 'mounted') {
      coordsRef.current = { x, y };
      if (needsUpdate !== true) setNeedsUpdate(true);
    } else if (state === 'unmounted') {
      toggleTimeoutClear();
      toggleTimeoutRef.current = setTimeout(() => {
        if (state === 'unmounted') {
          coordsRef.current = { x, y };
          onToggle?.({ value: true });
        }
      }, delay);
    }
  };

  const focusInitProps = {
    onFocus: useCallback(
      (event: FocusEvent) => {
        if (
          event.eventPhase === Event.AT_TARGET ||
          (event.eventPhase === Event.BUBBLING_PHASE && isDatatip)
        ) {
          targetRef.current = event.target as HTMLElement;
        }
      },
      [isDatatip]
    ),

    onBlur: useCallback((event: FocusEvent) => {
      if (event.eventPhase === Event.AT_TARGET) {
        hiddenWithEsc.current = false;
      }
    }, [])
  };

  const hoverInitProps = {
    onMouseEnter: useCallback((event: MouseEvent) => {
      targetRef.current = event.target as HTMLElement;
    }, [])
  };

  const keyInitProps = {
    onKeyUp: useCallback(
      (event: KeyboardEvent) => {
        if (event.code === 'Escape') {
          event.preventDefault();
          hiddenWithEsc.current = true;
          onToggle?.({ value: false });
        }
      },
      [onToggle]
    )
  };

  let actionableProps;

  const __resetCoords = () => {
    coordsRef.current = { x: -9999, y: -9999 };
  };

  if (disabled) {
    actionableProps = mergeProps(hoverProps, focusProps, hoverInitProps, focusInitProps);
  } else {
    const anchorInitProps = {
      'aria-describedby': uniqueIdRef.current,

      ...(isPointer && {
        onMouseLeave: () => {
          if (state === 'mounting') {
            return;
          }
          toggleTimeoutClear();
        },
        onMouseMove: (event: MouseEvent) => {
          handleMouseMove(event);
        }
      })
    };
    actionableProps = mergeProps(
      hoverProps,
      focusProps,
      touchProps,
      hoverInitProps,
      focusInitProps,
      anchorInitProps,
      keyInitProps
    );
  }

  // No need to process tooltip if disabled or no text is provided
  // Include actionableProps to detect mouseenter in components
  // hovered state won't update unless actionableProps are already present in the component when mouse enters
  if (disabled) {
    toggleTimeoutClear();
    __resetCoords();
    return {
      tooltipContent: null,
      tooltipProps: actionableProps
    };
  }

  const placement = positionToPlacement(position);

  const handleTransitionEnd = () => {
    if (state === 'mounting') {
      setState('mounted');
    }
    if (state === 'unmounting') {
      __resetCoords();
      setState('unmounted');
    }
  };

  const renderRawContent = (
    <TooltipContent
      id={uniqueIdRef.current}
      testId={testId}
      isOpen={['mounting', 'mounted', 'unmountPending'].includes(state)}
      {...popoverHoverProps}
      isDatatip={isDatatip}
      onTransitionEnd={handleTransitionEnd}>
      {text}
    </TooltipContent>
  );

  const renderTooltipOrDatatip = () => {
    if (variant === 'tooltip') {
      return (
        <EnvironmentProvider environment={{ colorScheme: newColorScheme }}>
          {renderRawContent}
        </EnvironmentProvider>
      );
    } else {
      return <>{renderRawContent}</>;
    }
  };

  const renderContent = (
    <Layer logicalParentRef={targetRef}>
      <Floating anchorRef={usedRef} placement={placement} offsetValue={offset}>
        {renderTooltipOrDatatip()}
      </Floating>
    </Layer>
  );

  return {
    tooltipContent: !['unmounted', 'mountPending'].includes(state) && renderContent,
    tooltipProps: actionableProps
  };
};
